<template>
  <div class="annotation-list">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6 class="mb-0">
        Pathologies ({{ annotations.length }})
      </h6>

      <!-- Measurements Toggle Icon (top right) -->
      <font-awesome-icon
        v-if="hasMeasurements"
        :icon="showMeasurements ? 'chevron-up' : 'chevron-down'"
        class="measurements-toggle-icon"
        @click="toggleMeasurements"
        :title="showMeasurements ? 'Hide measurements' : 'Show measurements'"
      />
    </div>

    <!-- Pathology Pills -->
    <div v-if="annotations.length > 0" class="pathology-pills-container">
      <div
        v-for="(annotation, index) in annotations"
        :key="annotation.id"
        class="pathology-pill-wrapper mb-2"
        :class="{ 'expanded': showMeasurements && hasMeasurementData(annotation) }"
      >
        <!-- Main Pill -->
        <div
          class="pathology-pill"
          :style="{
            backgroundColor: getPathologyColor(annotation.pathology),
            borderColor: annotation.id === selectedAnnotationId ? '#f0b93b' : 'transparent'
          }"
          @click="selectAnnotation(annotation.id)"
        >
          <div class="pill-main-content">
            <span class="pill-index">{{ index + 1 }}</span>
            <span class="pill-name">{{ annotation.pathology || 'Unlabeled' }}</span>
            <BBadge
              v-if="annotation.isAI"
              bg="dark"
              class="ms-2 pill-badge"
            >
              AI
            </BBadge>
          </div>

          <div class="pill-actions">
            <!-- Edit/Save button (hidden for version 2) -->
            <font-awesome-icon
              v-if="!isVersion2 && !isEditingAnnotation(annotation.id)"
              icon="edit"
              class="pill-action-icon"
              @click.stop="startEditing(annotation.id)"
              title="Edit annotation"
            />
            <font-awesome-icon
              v-else-if="!isVersion2"
              icon="check"
              class="pill-action-icon"
              @click.stop="stopEditing()"
              title="Save changes"
            />

            <font-awesome-icon
              v-if="!isVersion2"
              icon="times"
              class="pill-action-icon"
              @click.stop="deleteAnnotation(annotation.id)"
              title="Delete annotation"
            />
          </div>
        </div>

        <!-- Expanded Measurements -->
        <div
          v-if="showMeasurements && hasMeasurementData(annotation)"
          class="pill-measurements"
          :style="{ backgroundColor: getPathologyColor(annotation.pathology) }"
        >
          <div class="measurements-grid">
            <!-- Mapped Stats -->
            <template v-if="annotation.mapped_stats && Object.keys(annotation.mapped_stats).length > 0">
              <div
                v-for="(planeData, planeName) in annotation.mapped_stats"
                :key="planeName"
                class="measurement-item"
              >
                <span class="measurement-label">{{ getPlaneLabel(planeName) }}:</span>
                <span class="measurement-value">
                  <template v-if="planeData && typeof planeData === 'object'">
                    <template v-if="planeData.mean !== undefined && planeData.mean !== null">
                      {{ planeData.mean.toFixed(2) }}{{ planeData.modalityUnit || '' }}
                    </template>
                    <template v-else-if="planeData.length !== undefined && planeData.length !== null">
                      {{ planeData.length.toFixed(2) }}{{ planeData.unit || 'mm' }}
                    </template>
                    <template v-else>
                      {{ JSON.stringify(planeData) }}
                    </template>
                  </template>
                  <template v-else-if="typeof planeData === 'number'">
                    {{ planeData.toFixed(2) }}
                  </template>
                  <template v-else>
                    N/A
                  </template>
                </span>
              </div>
            </template>

            <!-- Other Measurements -->
            <template v-if="annotation.other && annotation.other.length > 0">
              <div
                v-for="(measurement, mIdx) in annotation.other"
                :key="`${measurement.view}-${mIdx}`"
                class="measurement-item"
              >
                <span class="measurement-label">
                  {{ measurement.type }} ({{ measurement.view?.trim() }}):
                </span>
                <span class="measurement-value">
                  {{ measurement.stats?.length?.toFixed(2) || 'N/A' }}{{ measurement.stats?.unit || 'mm' }}
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="annotations.length === 0"
      class="text-muted text-center py-4"
    >
      <p class="mb-0">No pathologies drawn yet.</p>
      <small>Select a pathology and drawing mode above to start annotating.</small>
    </div>

    <!-- Delete Confirmation Modal -->
    <BModal
      v-model="showDeleteModal"
      title-class="text-white h4"
      title="Delete Annotation"
      ok-title="Delete"
      cancel-title="Cancel"
      ok-variant="danger"
      cancel-variant="secondary"
      @ok="confirmDelete"
      @cancel="cancelDelete"
    >
      <p class="mb-0">
        Are you sure you want to delete this pathology annotation?
      </p>
      <p v-if="annotationToDelete" class="fw-bold mt-2 mb-0" style="color: #f0b93b;">
        {{ getAnnotationName(annotationToDelete) }}
      </p>
    </BModal>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'

const props = defineProps({
  project: {
    type: Object,
    default: null
  }
})

const store = useStore()

const showMeasurements = ref(false)
const showDeleteModal = ref(false)
const annotationToDelete = ref(null)

const annotations = computed(() => store.state.labelerAnnotation.annotations)
const selectedAnnotationId = computed(() => store.state.labelerAnnotation.selectedAnnotationId)
const editingAnnotationId = computed(() => store.state.labelerAnnotation.editingAnnotationId)
const isVersion2 = computed(() => props.project?.version === 2)

// Check if any annotations have measurements
const hasMeasurements = computed(() => {
  return annotations.value.some(ann => hasMeasurementData(ann))
})

// Check if specific annotation has measurement data
const hasMeasurementData = (annotation) => {
  return (annotation.other && annotation.other.length > 0) ||
         (annotation.mapped_stats && Object.keys(annotation.mapped_stats).length > 0)
}

// Get plane label for display
const getPlaneLabel = (planeName) => {
  const labels = {
    xy: 'Axial (Transverse)',
    xz: 'Coronal (Frontal)',
    yz: 'Sagittal'
  }
  return labels[planeName] || planeName
}

// Get pathology color from project config
const getPathologyColor = (pathologyName) => {
  if (!props.project?.pathologies) return '#ed1a4f' // Default color

  const pathology = props.project.pathologies.find(p =>
    p.label === pathologyName || p.value === pathologyName
  )

  return pathology?.color || '#ed1a4f'
}

const toggleMeasurements = () => {
  showMeasurements.value = !showMeasurements.value

  // Debug logging with searchable prefix
  console.log('MEASUREMENTS_DEBUG: Toggle clicked, new state:', showMeasurements.value)
  console.log('MEASUREMENTS_DEBUG: Total annotations:', annotations.value.length)

  if (showMeasurements.value) {
    annotations.value.forEach((ann, idx) => {
      console.log(`MEASUREMENTS_DEBUG: Annotation ${idx + 1} (${ann.pathology}):`, {
        id: ann.id,
        hasMapped: !!ann.mapped_stats,
        hasOther: !!ann.other,
        mapped_stats_raw: JSON.parse(JSON.stringify(ann.mapped_stats || {})),
        other_raw: JSON.parse(JSON.stringify(ann.other || []))
      })

      // Check each plane in mapped_stats
      if (ann.mapped_stats) {
        Object.keys(ann.mapped_stats).forEach(planeName => {
          const planeData = ann.mapped_stats[planeName]
          console.log(`MEASUREMENTS_DEBUG:   Plane ${planeName}:`, {
            isNull: planeData === null,
            isUndefined: planeData === undefined,
            type: typeof planeData,
            keys: planeData ? Object.keys(planeData) : [],
            value: JSON.parse(JSON.stringify(planeData || {})),
            hasMean: planeData && 'mean' in planeData,
            mean: planeData?.mean,
            hasModalityUnit: planeData && 'modalityUnit' in planeData,
            modalityUnit: planeData?.modalityUnit
          })
        })
      }

      // Check other measurements
      if (ann.other && Array.isArray(ann.other)) {
        ann.other.forEach((measurement, mIdx) => {
          console.log(`MEASUREMENTS_DEBUG:   Other measurement ${mIdx + 1}:`, {
            type: measurement.type,
            view: measurement.view,
            hasStats: !!measurement.stats,
            stats: JSON.parse(JSON.stringify(measurement.stats || {}))
          })
        })
      }
    })
  }
}

const selectAnnotation = (id) => {
  store.dispatch('labelerAnnotation/selectAnnotation', id)
}

const deleteAnnotation = (annotationId) => {
  annotationToDelete.value = annotationId
  showDeleteModal.value = true
}

const confirmDelete = () => {
  if (annotationToDelete.value) {
    store.dispatch('labelerAnnotation/deleteAnnotation', annotationToDelete.value)
  }
  cancelDelete()
}

const cancelDelete = () => {
  annotationToDelete.value = null
  showDeleteModal.value = false
}

const getAnnotationName = (annotationId) => {
  const annotation = annotations.value.find(ann => ann.id === annotationId)
  return annotation?.pathology || 'Unlabeled'
}

const isEditingAnnotation = (id) => {
  return editingAnnotationId.value === id
}

const startEditing = (annotationId) => {
  console.log('Start editing annotation:', annotationId)
  // This automatically stops editing any other annotation since only one can be in edit mode
  store.commit('labelerAnnotation/SET_EDITING_ANNOTATION', annotationId)
  store.dispatch('labelerAnnotation/selectAnnotation', annotationId)
}

const stopEditing = () => {
  console.log('Stop editing annotation')
  store.commit('labelerAnnotation/SET_EDITING_ANNOTATION', null)
}
</script>

<style scoped>
.annotation-list {
  padding: 1rem;
  background-color: var(--color-bg-secondary);
  border-radius: var(--border-radius-standard);
  max-height: 400px;
  overflow-y: auto;
  border: var(--border-width-card) solid var(--color-border-secondary);
}

.annotation-list h6 {
  color: #ffffff;
  font-weight: 900;
  letter-spacing: 0.05em;
}

/* Measurements Toggle Icon */
.measurements-toggle-icon {
  color: #3981c8;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s;
}

.measurements-toggle-icon:hover {
  color: #f0b93b;
  transform: scale(1.1);
}

/* Pills Container */
.pathology-pills-container {
  display: flex;
  flex-direction: column;
}

.pathology-pill-wrapper {
  border-radius: 1.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
}

.pathology-pill-wrapper.expanded {
  border-radius: 0.75rem;
}

/* Main Pill */
.pathology-pill {
  padding: 0.5rem 0.75rem;
  border: 2px solid transparent;
  border-radius: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Remove bottom border radius when expanded */
.pathology-pill-wrapper.expanded .pathology-pill {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.pathology-pill:hover {
  transform: scale(1.02);
  box-shadow: 0 0 10px rgba(240, 185, 59, 0.4);
}

.pill-main-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.pill-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background-color: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 900;
  margin-right: 0.5rem;
  color: #ffffff;
}

.pill-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #ffffff;
}

.pill-badge {
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
}

.pill-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: 0.5rem;
}

.pill-action-icon {
  cursor: pointer;
  color: #ffffff;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.pill-action-icon:hover {
  color: #f0b93b;
  transform: scale(1.15);
}

/* Pill Measurements (Expanded Section) */
.pill-measurements {
  padding: 0.75rem 1rem;
  background-color: rgba(0, 0, 0, 0.2);
  border-bottom-left-radius: 0.75rem;
  border-bottom-right-radius: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.measurements-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
}

.measurement-item {
  display: flex;
  flex-direction: column;
  font-size: 0.75rem;
}

.measurement-label {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.measurement-value {
  color: #ffffff;
  font-weight: 700;
  font-size: 0.85rem;
}

.text-muted {
  color: #878787 !important;
}

/* Delete Modal Styling */
:deep(.modal-content) {
  background-color: #15385b;
  color: #ffffff;
  border: 2px solid #f0b93b;
}

:deep(.modal-header) {
  background-color: var(--color-bg-primary);
  border-bottom: 2px solid #dc3545;
}

:deep(.modal-title) {
  color: #ffffff !important;
  font-weight: 900;
  letter-spacing: 0.05em;
}

:deep(.modal-header .btn-close) {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

:deep(.modal-header .btn-close:hover) {
  opacity: 1;
}

:deep(.modal-body) {
  color: #ffffff;
}

:deep(.modal-footer) {
  background-color: #15385b;
  border-top: 1px solid #3981c8;
}

:deep(.modal-footer .btn-danger) {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #ffffff;
  font-weight: 900;
}

:deep(.modal-footer .btn-danger:hover) {
  background-color: #c82333;
  border-color: #bd2130;
}

:deep(.modal-footer .btn-secondary) {
  background-color: #3981c8;
  border-color: #3981c8;
  color: #ffffff;
  font-weight: 900;
}

:deep(.modal-footer .btn-secondary:hover) {
  background-color: #2e6ba3;
  border-color: #2e6ba3;
}
</style>
